import React, { useState, useEffect, useCallback } from 'react';
import VoiceCloning from '../VoiceCloning';

/**
 * Overview tab — at-a-glance stats + quick actions for one managed
 * account. Stats: memory count, next reminder, unresolved alert count.
 * Actions: Call now, View memories (hands off to the main memory view).
 */
export default function CareOverviewTab({ account, token, api, onCallNow, onViewMemories, calling, onVoiceChanged }) {
  const [stats, setStats] = useState({ memories: null, nextReminder: null, openAlerts: null });
  const [recentCalls, setRecentCalls] = useState(null);
  const [voiceId, setVoiceId] = useState(account.companion_voice_id || null);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [presetVoices, setPresetVoices] = useState([]);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState('');

  const load = useCallback(async () => {
    // The dashboard can briefly render with the sentinel {id:null} account
    // (heart-icon open) before fetchAccounts resolves — skip the /null fetches.
    if (!account?.id) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    // Memories count (delegated read).
    try {
      const r = await fetch(`${api}/retrieve_memories?as_user_id=${encodeURIComponent(account.id)}`, auth);
      if (r.ok) { const d = await r.json(); setStats(s => ({ ...s, memories: (d.memories || []).length })); }
    } catch { /* leave null */ }
    // Next active reminder.
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/reminders`, auth);
      if (r.ok) {
        const d = await r.json();
        const active = (d.reminders || []).filter(x => x.status === 'active').sort((a, b) => new Date(a.next_fire_at) - new Date(b.next_fire_at));
        setStats(s => ({ ...s, nextReminder: active[0] ? new Date(active[0].next_fire_at) : 'none' }));
      }
    } catch { /* leave null */ }
    // Open (unresolved) alerts for this account.
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/crisis-events`, auth);
      if (r.ok) { const d = await r.json(); setStats(s => ({ ...s, openAlerts: (d.crisis_events || []).filter(e => !e.resolved_at).length })); }
    } catch { /* leave null */ }
    // Recent call summaries.
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/call-summaries`, auth);
      if (r.ok) { const d = await r.json(); setRecentCalls((d.call_summaries || []).slice(0, 3)); }
    } catch { /* leave null */ }
  }, [api, token, account.id]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh stats + recent call summaries so a summary that lands after a
  // call (generated post-call, asynchronously) shows up without a page refresh.
  useEffect(() => {
    const t = setInterval(() => { load(); }, 25000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      // Ready-made preset voices (no cloning/consent needed).
      try {
        const r = await fetch(`${api}/api/managed-accounts/voice-catalog`, auth);
        if (r.ok && alive) { const d = await r.json(); setPresetVoices(d.presets || []); }
      } catch { /* ignore */ }
      // The caregiver's own cloned voices (familiar-voice flow).
      try {
        const r = await fetch(`${api}/cloned_voices`, auth);
        if (r.ok && alive) setClonedVoices(await r.json() || []);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [api, token]);

  // Keep local voiceId in sync if the account prop changes (account switch).
  useEffect(() => { setVoiceId(account.companion_voice_id || null); }, [account.id, account.companion_voice_id]);

  const patchVoice = async (newVoiceId) => {
    setVoiceMsg('');
    try {
      const r = await fetch(`${api}/api/managed-accounts/${encodeURIComponent(account.id)}/voice`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ voice_id: newVoiceId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setVoiceId(d.companion_voice_id || null);
      // Propagate up so the parent's account list stays in sync — otherwise
      // switching accounts and back re-mounts this tab from the stale prop and
      // the change appears to "revert".
      onVoiceChanged?.(account.id, d.companion_voice_id || null);
      setVoiceMsg(newVoiceId ? 'Companion voice updated.' : 'Reset to the default voice.');
    } catch (e) { setVoiceMsg(`Couldn't update voice: ${e.message}`); }
  };

  const currentVoiceName = voiceId
    ? (presetVoices.find(v => v.voice_id === voiceId)?.name
        || clonedVoices.find(v => v.voice_id === voiceId)?.name
        || 'Custom voice')
    : 'Default companion voice';

  const fmt = (v) => v === null ? '…' : v;
  const fmtReminder = (v) => v === null ? '…' : v === 'none' ? 'None' : v.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div>
      <div className="care-stat-grid">
        <div className="care-stat">
          <div className="care-stat-value">{fmt(stats.memories)}</div>
          <div className="care-stat-label">Memories captured</div>
        </div>
        <div className="care-stat">
          <div className="care-stat-value" style={{ fontSize: 16 }}>{fmtReminder(stats.nextReminder)}</div>
          <div className="care-stat-label">Next reminder</div>
        </div>
        <div className="care-stat">
          <div className="care-stat-value" style={{ color: stats.openAlerts > 0 ? '#f87171' : undefined }}>{fmt(stats.openAlerts)}</div>
          <div className="care-stat-label">Open alerts</div>
        </div>
      </div>
      <div className="care-action-row">
        <button className="care-btn green" onClick={onCallNow} disabled={calling}>
          {calling ? 'Calling…' : '📞 Call now'}
        </button>
        {onViewMemories && (
          <button className="care-btn" onClick={onViewMemories} disabled={!account?.id}>View memories →</button>
        )}
      </div>

      <div style={{ marginTop: 22 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(254,252,246,0.45)', marginBottom: 10 }}>
          Recent calls
        </div>
        {recentCalls === null ? (
          <div className="care-skeleton" />
        ) : recentCalls.length === 0 ? (
          <div className="care-empty-state" style={{ padding: '18px 8px' }}>No call summaries yet. They'll appear here after a companion call.</div>
        ) : (
          recentCalls.map((c) => (
            <div key={c.id} className="care-list-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="care-row-meta">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</div>
                <div className="care-row-body">{c.summary}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: 22, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 18 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(254,252,246,0.45)', marginBottom: 8 }}>
          Companion voice
        </div>
        <div className="care-row-body" style={{ marginBottom: 10 }}>
          Current: <strong>{currentVoiceName}</strong>
          {voiceId && (
            <button className="care-mini-btn" style={{ marginLeft: 10 }} onClick={() => patchVoice(null)}>Use default voice</button>
          )}
        </div>
        {voiceMsg && <div style={{ fontSize: 12, color: '#7fa876', marginBottom: 8 }}>{voiceMsg}</div>}

        {presetVoices.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div className="care-row-meta" style={{ marginBottom: 8 }}>Pick a ready-made voice — no recording or consent needed:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {presetVoices.map((v) => (
                <button
                  key={v.voice_id}
                  className={`care-mini-btn${voiceId === v.voice_id ? ' active' : ''}`}
                  onClick={() => patchVoice(v.voice_id)}
                  title={v.name}
                >
                  {v.description || v.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="care-row-meta" style={{ marginBottom: 8 }}>…or use a familiar voice:</div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: 'rgba(254,252,246,0.75)', marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={voiceConsent} onChange={(e) => setVoiceConsent(e.target.checked)} style={{ marginTop: 2 }} />
          <span>I have {account.name || 'this person'}'s consent (or the family's) to clone and use a familiar voice for their companion. Calls use ElevenLabs voices.</span>
        </label>
        {voiceConsent ? (
          <VoiceCloning
            api={api}
            token={token}
            currentVoiceId={voiceId}
            onSelectVoice={(vid) => patchVoice(vid)}
            onVoiceCloned={(vid) => patchVoice(vid)}
            showToast={(m) => setVoiceMsg(typeof m === 'string' ? m : '')}
          />
        ) : (
          <div className="care-row-meta">Check the box above to choose or record a familiar voice.</div>
        )}
      </div>
    </div>
  );
}
